import { has, uniq } from 'lodash';
import {
  all,
  fork,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'typed-redux-saga/macro';
import { modules } from '@veritone/glc-redux';

import { CREATE_ENGINE_JOB as CREATE_OT_JOB } from '../../engines/optical-tracking/actions';
import { CREATE_ENGINE_JOB as CREATE_TRANSCRIPTION_JOB } from '../../engines/transcription/actions';
import {
  BULK_ADD_BACKGROUND_BLUR,
  CHANGE_CODE,
  CHANGE_SHAPE,
  CHANGE_TEXT_OVERLAY,
  IN_DOWNLOAD_ASSET,
  IN_REDACT_MEDIA,
  IN_SEND_TO_HEAD_DETECTION,
  SET_DETECTION_TYPE_REDACTION,
  SET_INDIVIDUAL_REDACTION,
} from '@worker';

import {
  DELETE_SELECTED_TDOS,
  UNLINK_SELECTED_TDOS,
  UPSERT_ORGANIZATION_SDO_SUCCESS,
} from '@redact-modules/mainPage/actions';
import {
  actionLogAuditEvent,
  DELETE_LAST_ASSET,
  FETCH_UPDATE_ASSET_ACTION,
  LOG_AUDIT_EVENT,
  NEW_OVERLAY,
  PLAY_REDACTED_ASSET,
  REDACT_WORDS,
  RESET_WORDS_LOG,
  RedactWordsRequest,
  RESIZE_OVERLAY_SEGMENT,
  ADD_TEXT_OVERLAY_SEGMENT,
  MERGE_TEXT_OVERLAY_SEGMENTS,
  SET_TEXT_OVERLAY_SINGLE_RANGE,
  DELETE_DETECTION_OVERLAY,
  DELETE_DETECTION_IN_FRAME,
  DELETE_DETECTION_SEGMENT,
  DELETE_DETECTION_GROUP,
  DELETE_UDR_OVERLAY,
  DELETE_UDR_IN_FRAME,
  DELETE_UDR_SEGMENT,
  DELETE_UDR_GROUP,
  ADD_REDACTION_CODE_TO_TRANSCRIPTION,
  REMOVE_REDACTION_CODE_FROM_TRANSCRIPTION,
  REDACT_SLICE,
  RESET_SLICE,
  MASK_VOICE_WORDS,
  MASK_SLICE,
  BACKGROUND_BLUR_SLICE,
  RESET_BACKGROUND_BLUR_SLICE,
  UPDATE_BACKGROUND_BLUR_LEVELS,
  STATUS_CHANGE,
} from '../actions';
import { resolveRedactionStatusDisplayLabel } from '../redactionStatuses';
import {
  selectCurrentPosition,
  selectTdoIsChanged,
  selectUdrAsset,
  selectWordsRedacted,
} from '../selectors';
import { selectRedactionCustomStatuses } from '@redact-modules/redactionStatuses/selectors';
import { sendEvent } from '../services';
import moment from 'moment';
import { GetActionCreatorPayloadT } from '@utils';

const {
  config: { getConfig },
  user: { selectUser },
} = modules;

interface Action<P, M = unknown> {
  type: string;
  payload: P;
  meta: M;
}

export function* auditLogsSaga() {
  yield* all([
    fork(onActionLogAuditEvent),
    fork(watchExternalMediaSentToRedact),
    fork(watchUpdateRedactionStatus),
    fork(watchSaveChanges),
    fork(watchPlayRedactedAsset),
    fork(watchDeleteLastAsset),
    fork(watchDownloadAsset),
    fork(watchRedactMediaFile),
    fork(watchOpticalTrackingJob),
    fork(watchHeadDetectionJob),
    fork(watchTranscriptionJob),
    fork(watchUnlinkTDOs),
    fork(watchDeleteTDOs),
    fork(watchRedactTranscriptionWords),
    fork(watchVoiceMaskTranscriptionWords),
    fork(watchIndividualBlurJob),
    fork(watchIndividualCodeJob),
    fork(watchBulkDetectionRedactionJob),
    fork(watchAddRedactionCodeToTranscription),
    fork(watchRemoveRedactionCodeFromTranscription),
    fork(watchResetTranscriptionWords),
    fork(watchTimelineAudioRedaction),
    fork(watchTimelineVoiceMask),
    fork(watchTimelineResetAudioSlice),
    fork(watchTimelineBackgroundBlurSlice),
    fork(watchTimelineResetBackgroundBlurSlice),
    fork(watchBulkAddBackgroundBlur),
    fork(watchUpdateBackgroundBlurLevels),
    fork(watchResizeOverlay),
    fork(watchDeleteUDRGroups),
    fork(watchDeleteUDR),
    fork(watchNewOverlay),
    fork(watchDeleteDetection),
    fork(watchChangeShapeBoundingBox),
    fork(watchTextOverlayEdits),
  ]);
}

export function* onActionLogAuditEvent() {
  yield* takeEvery(LOG_AUDIT_EVENT, function* (action) {
    const { eventSchemaId } = yield* select(getConfig<Window['config']>);
    yield* put(sendEvent(eventSchemaId, action));
  });
}

// ////////////////////////////////////////
// Watch for events to log

function* watchExternalMediaSentToRedact() {
  yield* takeLatest(UPSERT_ORGANIZATION_SDO_SUCCESS, function* (action) {
    if (!has(action, 'meta.variables.id')) {
      yield* put(actionLogAuditEvent('User sent media file to Redact app'));
    }
  });
}

function* watchUpdateRedactionStatus() {
  yield* takeLatest(
    // FETCH_STATUS_CHANGE_SUCCESS, // avoid for now
    STATUS_CHANGE,
    watchUpdateRedactionStatusHandle
  );
}

export function* watchUpdateRedactionStatusHandle({
  payload,
  meta,
}: ReturnType<typeof STATUS_CHANGE> & {
  meta?: { isLocalRollback?: boolean };
}) {
  if (meta?.isLocalRollback) {
    return;
  }
  // if using FETCH_STATUS_CHANGE_SUCCESS action would extract this way - however this causing update on every save since that action is used to update tdo modified time as well
  // const tag =
  //     findLast(
  //       payload.updateTDO.details.tags,
  //       (item) => item?.value === IN_REDACTION_TAG_KEY
  //     ) ?? null;
  // const redactionStatus = tag?.redactionStatus || '';

  // Log the human-readable status name, never the identity. `payload.status` is
  // the canonical identity, which for a custom status is its `statusKey` UUID —
  // logging it verbatim put raw UUIDs in the audit trail (VE-27621). The status
  // picker freezes the name it displayed onto `statusLabel`, so that wins: it is
  // the point-in-time name, correct even if the status is later renamed or
  // deleted. Programmatic dispatches (To Do -> Draft auto-promote, locked-status
  // replay, rollbacks) send only `status`, so those resolve against the org's
  // custom statuses; an unresolvable value falls back to Unknown, and an absent
  // one to the default label, both inside the resolver.
  const customStatuses = yield* select(selectRedactionCustomStatuses);
  const frozenStatusLabel = payload.statusLabel?.trim();
  const redactionStatus =
    frozenStatusLabel ||
    resolveRedactionStatusDisplayLabel(payload.status, customStatuses);

  yield* put(
    actionLogAuditEvent('Redaction Status updated to: ' + redactionStatus)
  ); // TODO would be nice to include status type!
}

function* watchSaveChanges() {
  yield* takeLatest(FETCH_UPDATE_ASSET_ACTION, watchSaveChangesAuditLogHandle);
}

export function* watchSaveChangesAuditLogHandle() {
  // have to insert AuditLog in name to avoid name conflict with other saga
  const isTdoChanged: boolean = yield* select(selectTdoIsChanged);
  if (isTdoChanged) {
    yield* put(actionLogAuditEvent('Save'));
  }
}

function* watchPlayRedactedAsset() {
  yield* takeLatest(PLAY_REDACTED_ASSET, function* () {
    yield* put(actionLogAuditEvent('View redacted asset'));
  });
}

function* watchDeleteLastAsset() {
  yield* takeLatest(DELETE_LAST_ASSET, function* () {
    yield* put(actionLogAuditEvent('Delete redacted asset'));
  });
}

export function* watchDownloadAsset() {
  yield* takeLatest(IN_DOWNLOAD_ASSET, function* () {
    yield* put(actionLogAuditEvent('Download redacted asset'));
  });
}

export function* redactMediaFile() {
  yield* put(actionLogAuditEvent('Redact Media File'));
  const words = yield* select(selectWordsRedacted); // VMASKTODO review this
  if (words.length > 0) {
    yield* put(actionLogAuditEvent('Redact Audio File'));
  }
}

export function* watchRedactMediaFile() {
  yield* takeLatest(IN_REDACT_MEDIA, redactMediaFile);
}

function* watchHeadDetectionJob() {
  yield* takeLatest(IN_SEND_TO_HEAD_DETECTION, function* () {
    yield* put(actionLogAuditEvent('Head Detection Run'));
  });
}
function* watchOpticalTrackingJob() {
  yield* takeLatest(CREATE_OT_JOB, function* ({ payload }) {
    yield* put(
      actionLogAuditEvent('Optical Tracking Run: ' + JSON.stringify(payload))
    );
  });
}
function* watchIndividualCodeJob() {
  yield* takeLatest(CHANGE_CODE, function* ({ payload }) {
    const currentPosition: number = yield* select(selectCurrentPosition);

    // when multiple groups this gets complicated
    const numGroups =
      payload.udrGroupIds.length + payload.detectionGroups.length;

    if (numGroups === 1) {
      const prevCode = yield* select(selectUdrAsset);

      // use legacy audit log messages
      const groupId =
        payload.udrGroupIds.length > 0
          ? payload.udrGroupIds[0]
          : payload.detectionGroups[0]!.id; // one guarnteed to exist from numGroups check above

      const previousRedactionCode = groupId
        ? prevCode.boundingPolys?.[groupId]?.redactionCode
        : undefined;

      if (payload.redactionCode) {
        if (previousRedactionCode) {
          yield* put(
            actionLogAuditEvent(
              `Activity: "Redaction Code Updated"\nNew Redaction Code:{ Code: ${JSON.stringify(
                payload.redactionCode?.code
              )} Color: ${JSON.stringify(
                payload.redactionCode?.codeColor
              )} Location: ${JSON.stringify(
                payload.redactionCode?.codeLocation
              )} }\nOld Redaction Code:{ Code: ${JSON.stringify(
                previousRedactionCode.code
              )} Color: ${JSON.stringify(
                previousRedactionCode.codeColor
              )} Location: ${JSON.stringify(
                previousRedactionCode.codeLocation
              )} }\nTime point in media: ${currentPosition} ms`
            )
          );
        } else {
          yield* put(
            actionLogAuditEvent(
              `Activity: "Redaction Code Added"\nRedaction Code:{ Code: ${JSON.stringify(
                payload.redactionCode?.code
              )} Color: ${JSON.stringify(
                payload.redactionCode?.codeColor
              )} Location: ${JSON.stringify(
                payload.redactionCode?.codeLocation
              )} }\nTime point in media: ${currentPosition} ms`
            )
          );
        }
      } else if (previousRedactionCode) {
        yield* put(
          actionLogAuditEvent(
            `Activity: "Redaction Code Deleted"\nOld Redaction Code:{ Code: ${JSON.stringify(
              previousRedactionCode.code
            )} Color: ${JSON.stringify(
              previousRedactionCode.codeColor
            )} Location: ${JSON.stringify(
              previousRedactionCode.codeLocation
            )} }\nTime point in media: ${currentPosition} ms`
          )
        );
      }
    } else {
      // merged group
      if (payload.redactionCode) {
        yield* put(
          actionLogAuditEvent(
            `Activity: "Redaction Code Added/Updated for Merged Group"\nRedaction Code:{ Code: ${JSON.stringify(
              payload.redactionCode?.code
            )} Color: ${JSON.stringify(
              payload.redactionCode?.codeColor
            )} Location: ${JSON.stringify(
              payload.redactionCode?.codeLocation
            )} }\nTime point in media: ${currentPosition} ms`
          )
        );
      } else {
        yield* put(
          actionLogAuditEvent(
            `Activity: "Redaction Code Deleted for Merged Group \nTime point in media: ${currentPosition} ms`
          )
        );
      }
    }
  });
}

function* watchIndividualBlurJob() {
  yield* takeLatest(SET_INDIVIDUAL_REDACTION, function* ({ payload }) {
    const currentUser = yield* select(selectUser);
    yield* put(
      actionLogAuditEvent(
        `Datetime: ${moment()}
          User Info: ${currentUser.email} ${currentUser.kvp?.firstName}  ${
            currentUser.kvp?.lastName
          } ${currentUser.userId}
          Activity: "Redaction Configuration changed: ${JSON.stringify(
            payload
          )}"
          Time Range:
            Start Time(ms): ${payload.startTimeMs}
            Stop Time(ms): ${payload.stopTimeMs}`
      )
    );
  });
}

function* watchBulkDetectionRedactionJob() {
  yield* takeLatest(SET_DETECTION_TYPE_REDACTION, function* ({ payload }) {
    const currentUser = yield* select(selectUser);
    yield* put(
      actionLogAuditEvent(
        `Datetime: ${moment()}
          User Info: ${currentUser.email} ${currentUser.kvp?.firstName}  ${
            currentUser.kvp?.lastName
          } ${currentUser.userId}
          Activity: "Redaction Configuration changed: ${JSON.stringify(
            payload
          )}"`
      )
    );
  });
}
function* watchNewOverlay() {
  yield* takeLatest([NEW_OVERLAY], function* ({ payload }) {
    yield* put(
      actionLogAuditEvent('Add Bounding Box: ' + JSON.stringify(payload))
    );
  });
}

function* watchResizeOverlay() {
  yield* takeLatest([RESIZE_OVERLAY_SEGMENT], function* ({ payload }) {
    yield* put(
      actionLogAuditEvent('Resize Overlay Segment: ' + JSON.stringify(payload))
    );
  });
}

// Text overlays are created via NEW_OVERLAY (logged as "Add Bounding Box") and
// moved/resized via RESIZE_OVERLAY_SEGMENT, but edits to their text/style and
// their time ranges have their own worker actions that weren't audited. Log
// them here so text-overlay changes are traceable like redaction-code edits.
function* watchTextOverlayEdits() {
  yield* takeEvery(CHANGE_TEXT_OVERLAY, function* ({ payload }) {
    yield* put(
      actionLogAuditEvent('Text Overlay Updated: ' + JSON.stringify(payload))
    );
  });
  yield* takeEvery(ADD_TEXT_OVERLAY_SEGMENT, function* ({ payload }) {
    yield* put(
      actionLogAuditEvent(
        'Text Overlay Time Range Added: ' + JSON.stringify(payload)
      )
    );
  });
  yield* takeEvery(MERGE_TEXT_OVERLAY_SEGMENTS, function* ({ payload }) {
    yield* put(
      actionLogAuditEvent(
        'Text Overlay Time Ranges Merged: ' + JSON.stringify(payload)
      )
    );
  });
  yield* takeEvery(SET_TEXT_OVERLAY_SINGLE_RANGE, function* ({ payload }) {
    yield* put(
      actionLogAuditEvent(
        'Text Overlay Time Range Set: ' + JSON.stringify(payload)
      )
    );
  });
}

function* watchDeleteDetection() {
  yield* takeLatest(
    [DELETE_DETECTION_OVERLAY, DELETE_DETECTION_IN_FRAME],
    function* ({ payload }) {
      yield* put(
        actionLogAuditEvent('Delete Bounding Box: ' + JSON.stringify(payload))
      );
    }
  );
}

function* watchDeleteUDR() {
  yield* takeLatest(
    [DELETE_UDR_OVERLAY, DELETE_UDR_IN_FRAME],
    function* ({ payload }) {
      yield* put(actionLogAuditEvent('Delete UDR: ' + JSON.stringify(payload)));
    }
  );
}

function* watchDeleteUDRGroups() {
  yield* takeLatest(
    [
      DELETE_DETECTION_SEGMENT,
      DELETE_UDR_SEGMENT,
      DELETE_DETECTION_GROUP,
      DELETE_UDR_GROUP,
    ],
    function* ({ payload }) {
      yield* put(
        actionLogAuditEvent(
          'Delete UDR Segment/Group: ' + JSON.stringify(payload)
        )
      );
    }
  );
}

function* watchTranscriptionJob() {
  yield* takeLatest(CREATE_TRANSCRIPTION_JOB, function* () {
    yield* put(actionLogAuditEvent('Transcribe Audio'));
  });
}

function* watchUnlinkTDOs() {
  yield* takeLatest(UNLINK_SELECTED_TDOS, function* () {
    yield* put(actionLogAuditEvent('Unlink TDOs from CMS'));
  });
}

function* watchDeleteTDOs() {
  yield* takeLatest(DELETE_SELECTED_TDOS, function* () {
    yield* put(actionLogAuditEvent('Delete TDOs'));
  });
}

function* handleAudioEditWordsLog(
  // RedactWordsRequest and ResetWordsRequest are identical
  isVoiceMask: boolean,
  action: Action<RedactWordsRequest /* | ResetWordsRequest */>
) {
  const { payload } = action;

  let uniqueWordsMsg = 'Words set for redaction';
  let detailsMsg = 'Audio Redaction Details';

  if (isVoiceMask) {
    uniqueWordsMsg = 'Words set for voice mask';
    detailsMsg = 'Voice Mask Details';
  }

  if (RESET_WORDS_LOG.match(action)) {
    uniqueWordsMsg = 'Words set for audio edit reset';
    detailsMsg = 'Audio Edit Reset Details';
  }
  const words = uniq(payload.map((t) => t.words)).join(', ');
  yield* put(actionLogAuditEvent(`${uniqueWordsMsg}: ${words}`));
  const wordsWithTimestamp = payload.map((t) =>
    JSON.stringify({
      words: t.words,
      startTimeMs: t.startTimeMs,
      stopTimeMs: t.stopTimeMs,
    })
  );
  yield* put(
    actionLogAuditEvent(`${detailsMsg}: \n ${wordsWithTimestamp.join(',\n')}`)
  );
}

function* watchRedactTranscriptionWords() {
  yield* takeLatest(REDACT_WORDS, handleAudioEditWordsLog, false);
}

function* watchVoiceMaskTranscriptionWords() {
  yield* takeLatest(MASK_VOICE_WORDS, handleAudioEditWordsLog, true);
}

function* watchResetTranscriptionWords() {
  yield* takeLatest(RESET_WORDS_LOG, handleAudioEditWordsLog, false);
}

function* watchAddRedactionCodeToTranscription() {
  yield* takeLatest(
    ADD_REDACTION_CODE_TO_TRANSCRIPTION,
    function* ({ payload }) {
      const { word } = payload;
      const message = word.editDetails?.redactionCode
        ? 'Redaction Code Updated'
        : 'Redaction Code Added';

      yield* put(actionLogAuditEvent(`${message} ${JSON.stringify(payload)}`));
    }
  );
}

function* watchRemoveRedactionCodeFromTranscription() {
  yield* takeLatest(
    REMOVE_REDACTION_CODE_FROM_TRANSCRIPTION,
    function* ({ payload }) {
      yield* put(
        actionLogAuditEvent(`Redaction Code Deleted ${JSON.stringify(payload)}`)
      );
    }
  );
}

function* handleTimelineAudioRedactionLogs(
  action: Action<GetActionCreatorPayloadT<typeof REDACT_SLICE>>
) {
  const { payload } = action;
  if ((payload.redact.length || 0) > 0) {
    const timeStamps = payload.redact.map((t) =>
      JSON.stringify({
        startTimeMs: t[0],
        stopTimeMs: t[1],
      })
    );
    yield* put(
      actionLogAuditEvent(
        `Timeline Audio Redaction Details: \n ${timeStamps.join(',\n')}`
      )
    );
  }
}

function* handleTimelineVoiceMaskLogs(
  action: Action<GetActionCreatorPayloadT<typeof MASK_SLICE>>
) {
  const { payload } = action;
  if ((payload.mask.length || 0) > 0) {
    const timeStamps = payload.mask.map((t) =>
      JSON.stringify({
        startTimeMs: t[0],
        stopTimeMs: t[1],
      })
    );
    yield* put(
      actionLogAuditEvent(
        `Timeline Audio Voice Mask Details: \n ${timeStamps.join(',\n')}`
      )
    );
  }
}

function* handleTimelineResetAudioSliceLogs(
  action: Action<GetActionCreatorPayloadT<typeof RESET_SLICE>>
) {
  const { payload } = action;
  if ((payload.length || 0) > 0) {
    const timeStamps = payload.map((t) =>
      JSON.stringify({
        startTimeMs: t[0],
        stopTimeMs: t[1],
      })
    );
    yield* put(
      actionLogAuditEvent(
        `Timeline Reset Audio Slice Details: \n ${timeStamps.join(',\n')}`
      )
    );
  }
}

function* handleTimelineBackgroundBlurLogs(
  action: Action<GetActionCreatorPayloadT<typeof BACKGROUND_BLUR_SLICE>>
) {
  const { payload } = action;

  const info = JSON.stringify({
    startTimeMs: payload.slice[0],
    stopTimeMs: payload.slice[1],
    blurLevel: payload.slice[2]?.blurLevel,
  });

  yield* put(actionLogAuditEvent(`Timeline Background Blur Slice: ${info}`));
}

function* handleTimelineResetBackgroundBlurLogs(
  action: Action<GetActionCreatorPayloadT<typeof RESET_BACKGROUND_BLUR_SLICE>>
) {
  const { payload } = action;
  if ((payload.length || 0) > 0) {
    const timeStamps = payload.map((t) =>
      JSON.stringify({
        startTimeMs: t[0],
        stopTimeMs: t[1],
      })
    );
    yield* put(
      actionLogAuditEvent(
        `Timeline Reset Background Blur Slices: \n ${timeStamps.join(',\n')}`
      )
    );
  }
}

function* handleUpdateBackgroundBlurLevels(
  action: Action<GetActionCreatorPayloadT<typeof UPDATE_BACKGROUND_BLUR_LEVELS>>
) {
  const { payload } = action;
  yield* put(
    actionLogAuditEvent(
      `Update All Background Blur Slices with Background Blur Level: ${payload.blurLevel}`
    )
  );
}

function* handleBulkAddBackgroundBlur(
  action: Action<GetActionCreatorPayloadT<typeof BULK_ADD_BACKGROUND_BLUR>>
) {
  const { payload } = action;
  yield* put(
    actionLogAuditEvent(
      `Bulk add background blur to all object of type: ${payload.detectionType}`
    )
  );
}

function* watchTimelineAudioRedaction() {
  yield* takeLatest([REDACT_SLICE], handleTimelineAudioRedactionLogs);
}

function* watchTimelineVoiceMask() {
  yield* takeLatest([MASK_SLICE], handleTimelineVoiceMaskLogs);
}

function* watchTimelineResetAudioSlice() {
  yield* takeLatest([RESET_SLICE], handleTimelineResetAudioSliceLogs);
}

function* watchTimelineBackgroundBlurSlice() {
  yield* takeLatest([BACKGROUND_BLUR_SLICE], handleTimelineBackgroundBlurLogs);
}

function* watchTimelineResetBackgroundBlurSlice() {
  yield* takeLatest(
    [RESET_BACKGROUND_BLUR_SLICE],
    handleTimelineResetBackgroundBlurLogs
  );
}

function* watchUpdateBackgroundBlurLevels() {
  yield* takeLatest(
    [UPDATE_BACKGROUND_BLUR_LEVELS],
    handleUpdateBackgroundBlurLevels
  );
}

function* watchBulkAddBackgroundBlur() {
  yield* takeLatest([BULK_ADD_BACKGROUND_BLUR], handleBulkAddBackgroundBlur);
}

function* watchChangeShapeBoundingBox() {
  yield* takeLatest(CHANGE_SHAPE, function* ({ payload }) {
    yield* put(
      actionLogAuditEvent(
        'Change Shape of Bounding Box: ' + JSON.stringify(payload)
      )
    );
  });
}
